import { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";

const EMPTY = { name: "", email: "", subject: "", message: "" };

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  console.log("ENV CHECK");
  console.log("SERVICE:", process.env.REACT_APP_EMAILJS_SERVICE_ID);
  console.log("TEMPLATE:", process.env.REACT_APP_EMAILJS_TEMPLATE_ID);
  console.log("PUBLIC:", process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);

    try {
      // Nachricht im Backend speichern (Admin Panel)
      await api.post("/public/contact", form);

      // Mail über EmailJS versenden
      try {
        const templateParams = {
          from_name: form.name,
          from_email: form.email,
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
        };

        const result = await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          templateParams,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );

        console.log("EmailJS Erfolg:", result);
      } catch (emailError) {
        console.error("EMAILJS FULL ERROR:", emailError);

        if (emailError?.text) {
          console.error("TEXT:", emailError.text);
        }

        if (emailError?.status) {
          console.error("STATUS:", emailError.status);
        }

        toast.warning(
          "Nachricht wurde gespeichert, aber die E-Mail konnte nicht versendet werden."
        );
      }

      setSent(true);
      setForm(EMPTY);

      toast.success(t("contact.toastSuccess"));
    } catch (err) {
      console.error("Backend Fehler:", err);

      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <aside className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-4">{t("contact.eyebrow")}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            {t("contact.title1")}<br /> {t("contact.title2")}
          </h1>
          <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-md">
            {t("contact.subtitle")}
          </p>

          <div className="mt-12 space-y-6 text-sm">
            <InfoRow icon={<Mail size={16} />} label={t("contact.emailLabel")} value="c.toker@refaheuropa.eu" />
          </div>

          <div className="mt-12 p-6 border border-bordeaux/20 rounded-sm bg-beige-light">
            <div className="text-xs uppercase tracking-widest text-bordeaux-accent mb-2">{t("contact.privacyTitle")}</div>
            <p className="text-xs text-slate-600 leading-relaxed">{t("contact.privacyText")}</p>
          </div>
        </aside>

        <section className="lg:col-span-7">
          {sent ? (
            <div
              data-testid="contact-success"
              className="bg-white border border-bordeaux/20 rounded-sm p-10 md:p-14 text-center"
            >
              <CheckCircle2 className="mx-auto text-bordeaux mb-5" size={36} />
              <h2 className="text-2xl font-semibold text-slate-900">{t("contact.successTitle")}</h2>
              <p className="text-slate-600 mt-3 max-w-md mx-auto">{t("contact.successText")}</p>
              <Button
                onClick={() => setSent(false)}
                data-testid="contact-new-btn"
                variant="outline"
                className="mt-8 rounded-sm border-slate-300 hover:bg-beige-light hover:text-slate-900"
              >
                {t("contact.successAgain")}
              </Button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white border border-slate-200 rounded-sm p-6 md:p-10 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label={t("contact.form.name")}>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("contact.form.namePlaceholder")}
                    data-testid="contact-name-input"
                    className="rounded-sm border-slate-300 focus:border-bordeaux focus:ring-bordeaux"
                  />
                </Field>
                <Field label={t("contact.form.email")}>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="name@example.com"
                    data-testid="contact-email-input"
                    className="rounded-sm border-slate-300 focus:border-bordeaux focus:ring-bordeaux"
                  />
                </Field>
              </div>
              <Field label={t("contact.form.subject")}>
                <Input
                  required
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder={t("contact.form.subjectPlaceholder")}
                  data-testid="contact-subject-input"
                  className="rounded-sm border-slate-300 focus:border-bordeaux focus:ring-bordeaux"
                />
              </Field>
              <Field label={t("contact.form.message")}>
                <Textarea
                  required
                  rows={8}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={t("contact.form.messagePlaceholder")}
                  data-testid="contact-message-input"
                  className="rounded-sm border-slate-300 focus:border-bordeaux focus:ring-bordeaux resize-y"
                />
              </Field>
              <div className="flex items-center justify-between pt-2 border-t border-slate-100 gap-4 flex-wrap">
                <p className="text-xs text-slate-500">{t("contact.form.consent")}</p>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="contact-submit-btn"
                  className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm px-6 py-5 font-medium"
                >
                  {busy ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" /> {t("contact.form.sending")}
                    </>
                  ) : (
                    <>
                      <Send size={14} className="mr-2" /> {t("contact.form.submit")}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <span className="w-9 h-9 grid place-items-center bg-bordeaux text-beige-light rounded-sm flex-shrink-0">
        {icon}
      </span>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-slate-900 font-medium mt-1">{value}</div>
      </div>
    </div>
  );
}
